import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import CategoryQuickEntry from "./components/CategoryQuickEntry";
import MediaCard from "@/components/feature/MediaCard";
import SectionTitle from "@/components/feature/SectionTitle";
import { getEditorPicks, getHotMedia, getLatestMedia } from "@/lib/media";

const container = "mx-auto w-full max-w-[1280px] px-4 md:px-6";

export default function Home() {
  const latest = getLatestMedia(8);
  const hot = getHotMedia(6);
  const picks = getEditorPicks(6);

  return (
    <div className="w-full">
      <HeroCarousel />

      <section className={`${container} py-10 md:py-14`}>
        <SectionTitle
          label="快速导航"
          title="热门影视分类"
          subtitle="按类型快速发现你想看的内容"
        />
        <CategoryQuickEntry />
      </section>

      <section className={`${container} pb-10 md:pb-14`}>
        <div className="flex flex-col gap-8 lg:flex-row">
          <div className="min-w-0 flex-1">
            <SectionTitle
              label="最新动态"
              title="最新影视资讯"
              subtitle="第一时间掌握院线与剧集上新"
            />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
              {latest.map((item, i) => (
                <MediaCard key={item.id} item={item} index={i} />
              ))}
            </div>
          </div>

          <aside className="w-full shrink-0 lg:w-[320px]">
            <div className="sticky top-24 rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-fire-fill text-primary-500 text-lg"></i>
                热播榜单
              </h2>
              <ul className="mt-4 space-y-1">
                {hot.map((item, i) => (
                  <li key={item.id}>
                    <Link
                      to={`/detail/${item.id}`}
                      className="group flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-background-50 cursor-pointer"
                    >
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${
                          i < 3
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-300 text-foreground-700"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="block truncate text-xs text-foreground-500">
                          {item.year} · {item.region}
                        </span>
                      </span>
                      <span className="flex items-center gap-0.5 text-xs font-semibold text-accent-600">
                        <i className="ri-star-fill text-[11px]"></i>
                        {item.rating.toFixed(1)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/category/movie"
                className="mt-3 flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-secondary-100 px-4 py-2.5 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer"
              >
                查看全部影视
                <i className="ri-arrow-right-s-line text-lg"></i>
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full bg-background-100 py-10 md:py-14">
        <div className={container}>
          <SectionTitle
            label="编辑精选"
            title="热门影视推荐"
            subtitle="编辑团队为你挑出的本期必看片单"
            actionText="更多推荐"
            actionTo="/category/movie"
          />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
            {picks.map((item, i) => (
              <MediaCard key={item.id} item={item} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className={`${container} py-12 md:py-16`}>
        <div className="relative overflow-hidden rounded-lg">
          <img
            src="https://readdy.ai/api/search-image?query=stylized%20cinematic%20wide%20banner%20of%20a%20dark%20movie%20theater%20with%20glowing%20deep%20orange%20screen%20light%20and%20empty%20seats%2C%20artistic%20mood%2C%20no%20text&width=1600&height=600&seq=hh-about-cta&orientation=landscape"
            alt="红狐影视 - 关于我们"
            title="红狐影视 + 影视资讯平台"
            className="h-[280px] w-full object-cover object-center md:h-[320px]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/92 via-secondary-950/75 to-secondary-950/40"></div>
          <div className="absolute inset-0 flex items-center px-6 md:px-12">
            <div className="max-w-2xl">
              <h2 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
                关于红狐影视
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-secondary-100 md:text-base">
                我们聚合最新电影与电视剧资讯、专业影评与编辑推荐，致力于帮助每一位影视爱好者高效发现值得观看的作品。从院线大片到口碑剧集，红狐影视陪你一起挑片、追剧、聊电影。
              </p>
              <div className="mt-6 flex flex-wrap gap-6">
                {[
                  { num: "1200+", label: "收录影视作品" },
                  { num: "300+", label: "专业影评" },
                  { num: "50万+", label: "影迷用户" },
                ].map((s) => (
                  <div key={s.label}>
                    <div className="font-heading text-xl font-extrabold text-accent-400 md:text-2xl">
                      {s.num}
                    </div>
                    <div className="text-xs text-secondary-300">{s.label}</div>
                  </div>
                ))}
              </div>
              <Link
                to="/about"
                className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                了解我们
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}